import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Stack,
} from "@mui/material";
import { ArrowBack, Edit, OpenInNew } from "@mui/icons-material";

const EntryDetail = () => {
  const { id } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const docRef = doc(db, "entries", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setEntry(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching entry:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEntry();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!entry) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Entry not found</Typography>
        <Button component={Link} to="/" variant="contained" sx={{ mt: 2 }}>
          Back to List
        </Button>
      </Box>
    );
  }

  const bd = entry.Bibliographical_details || {};
  const paratextKeys = Object.entries(entry.Paratexts || {})
    .filter(([k, v]) => k !== "is_paratext" && k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (entry.Paratexts?.other) paratextKeys.push(entry.Paratexts.other);

  const genreKeys = Object.entries(entry.Genre_subgenre || {})
    .filter(([k, v]) => k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (entry.Genre_subgenre?.other) genreKeys.push(entry.Genre_subgenre.other);

  const summaryKeywords = Object.entries(entry.Summary?.keywords || {})
    .filter(([k, v]) => k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (entry.Summary?.keywords?.other)
    summaryKeywords.push(entry.Summary.keywords.other);

  const titleKeywords = Object.entries(
    entry.Title?.title_subtitle_keyword || {},
  )
    .filter(([k, v]) => k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (entry.Title?.title_subtitle_keyword?.other)
    titleKeywords.push(entry.Title.title_subtitle_keyword.other);

  const dedicatees = Array.isArray(entry.Dedicatee_s)
    ? entry.Dedicatee_s.filter((d) => d.name)
    : [];

  const sources = [
    entry.Source?.main_source_1,
    entry.Source?.main_source_2,
    entry.Source?.main_source_3,
  ].filter((s) => s?.title);

  const settingTime = Object.entries(entry.Setting?.time || {})
    .filter(([k, v]) => k !== "ancient_time" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (entry.Setting?.time?.ancient_time)
    settingTime.push(entry.Setting.time.ancient_time);

  const settingPlace = [
    entry.Setting?.place?.england && "England",
    entry.Setting?.place?.the_moon && "The Moon",
    entry.Setting?.place?.europe && `Europe: ${entry.Setting.place.europe}`,
    entry.Setting?.place?.the_americas &&
      `The Americas: ${entry.Setting.place.the_americas}`,
    entry.Setting?.place?.africa && `Africa: ${entry.Setting.place.africa}`,
    entry.Setting?.place?.asia && `Asia: ${entry.Setting.place.asia}`,
    entry.Setting?.place?.other_worlds_imaginary &&
      `Other: ${entry.Setting.place.other_worlds_imaginary}`,
  ].filter(Boolean);

  const socialLoci = Object.entries(entry.Setting?.social_loci || {})
    .filter(([, v]) => v === true)
    .map(([k]) => k.replace(/_/g, " "));

  const locationTopoi = Object.entries(entry.Setting?.location_topoi || {})
    .filter(([k, v]) => k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (entry.Setting?.location_topoi?.other)
    locationTopoi.push(entry.Setting.location_topoi.other);

  const SectionHeader = ({ children }) => (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1976d2" }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 1, mb: 1 }} />
    </Grid>
  );

  const Field = ({ label, value, fullWidth = false }) =>
    value ? (
      <Grid item xs={12} md={fullWidth ? 12 : 6}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      </Grid>
    ) : null;

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1000, margin: "0 auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{ color: "#1976d2", fontWeight: "bold" }}
          >
            Text
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              component={Link}
              to={`/edit/${id}`}
              variant="contained"
              color="warning"
              startIcon={<Edit />}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Edit
            </Button>
            <Button
              component={Link}
              to="/"
              variant="outlined"
              startIcon={<ArrowBack />}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Back
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          {/* AUTHOR */}
          <SectionHeader>Author Information</SectionHeader>
          <Field label="Name" value={entry.Author?.name} />
          <Field
            label="Gender Attribution"
            value={entry.Author?.gender_attribution}
          />
          <Field label="Pseudonym" value={entry.Author?.pseudonym} />
          <Field label="Initials" value={entry.Author?.initials} />
          <Field label="Attribution" value={entry.Author?.attribution} />
          <Field
            label="Source of Attribution"
            value={entry.Author?.source_of_attribution}
          />

          {/* TITLE */}
          <SectionHeader>Title Information</SectionHeader>
          <Field label="Full Title" value={entry.Title?.full_title} fullWidth />
          <Field label="Short Version" value={entry.Title?.short_version} />
          {titleKeywords.length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Title Keywords
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {titleKeywords.join(", ")}
              </Typography>
            </Grid>
          )}

          {/* SOURCES */}
          {sources.length > 0 && (
            <>
              <SectionHeader>Source(s)</SectionHeader>
              {sources.map((s, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Source {i + 1}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {[s.title, s.author && `(${s.author})`, s.language, s.genre]
                      .filter(Boolean)
                      .join(" — ")}
                  </Typography>
                </Grid>
              ))}
            </>
          )}

          {/* TRANSLATION */}
          <SectionHeader>Translation</SectionHeader>
          {entry.Translation?.actual_translation && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Translation
              </Typography>
              <Chip label="Yes" size="small" color="success" />
            </Grid>
          )}
          <Field
            label="Translator's name"
            value={entry.Translation?.translator_name}
          />
          <Field
            label="Translator's gender"
            value={entry.Translation?.translator_gender}
          />
          <Field
            label="Original Title"
            value={entry.Translation?.original_title}
          />
          <Field
            label="Original Language"
            value={entry.Translation?.original_language}
          />
          {entry.Translation?.includes_verse && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Includes Verse
              </Typography>
              <Chip label="Yes" size="small" />
            </Grid>
          )}

          {/* BIBLIOGRAPHICAL DETAILS */}
          <SectionHeader>Bibliographical Details</SectionHeader>
          <Field
            label="Year of first edition"
            value={bd.year_of_first_edition}
          />
          <Field label="Place of Publication" value={bd.place_of_publication} />
          <Field label="Format" value={bd.format} />
          <Field label="Bookseller" value={bd.bookseller_s} />
          <Field label="Citation number" value={bd.citation_number} />
          <Field label="Printer" value={bd.printer} />
          <Field label="Other Editions" value={bd.other_editions} fullWidth />
          {bd.pdf_url && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                href={bd.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNew />}
              >
                View PDF
              </Button>
            </Grid>
          )}

          {/* GENRE / SUBGENRE */}
          {genreKeys.length > 0 && (
            <>
              <SectionHeader>Genre / Subgenre</SectionHeader>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {genreKeys.join(", ")}
                </Typography>
              </Grid>
            </>
          )}

          {/* PARATEXTS */}
          {paratextKeys.length > 0 && (
            <>
              <SectionHeader>Paratexts</SectionHeader>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {paratextKeys.join(", ")}
                </Typography>
              </Grid>
            </>
          )}

          {/* SETTING */}
          {(settingTime.length > 0 ||
            settingPlace.length > 0 ||
            socialLoci.length > 0 ||
            locationTopoi.length > 0) && (
            <>
              <SectionHeader>Setting</SectionHeader>
              {settingTime.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Time
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {settingTime.join(", ")}
                  </Typography>
                </Grid>
              )}
              {settingPlace.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Place
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {settingPlace.join(", ")}
                  </Typography>
                </Grid>
              )}
              {socialLoci.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Social Loci
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {socialLoci.join(", ")}
                  </Typography>
                </Grid>
              )}
              {locationTopoi.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location Topoi
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {locationTopoi.join(", ")}
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {/* SUMMARY */}
          {(summaryKeywords.length > 0 || entry.Summary?.short_summary) && (
            <>
              <SectionHeader>Summary</SectionHeader>
              {summaryKeywords.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Keywords
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {summaryKeywords.join(", ")}
                  </Typography>
                </Grid>
              )}
              <Field
                label="Short Summary"
                value={entry.Summary?.short_summary}
                fullWidth
              />
            </>
          )}

          {/* DEDICATEES */}
          {dedicatees.length > 0 && (
            <>
              <SectionHeader>Dedicatees</SectionHeader>
              {dedicatees.map((d, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dedicatee {i + 1}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {d.name}
                    {d.gender ? ` (${d.gender})` : ""}
                  </Typography>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default EntryDetail;
